import api from './api';

export interface Promotion {
  id: number;
  name: string;
  code: string;
  description?: string;
  discount_type: 'percentage' | 'fixed_amount' | 'buy_x_get_y';
  discount_value: number;
  buy_quantity?: number;
  get_quantity?: number;
  buy_service_id?: string;
  get_service_id?: string;
  status: 'active' | 'inactive' | 'expired';
  start_date: string;
  end_date: string;
  expiration_date: string;
  max_uses?: number;
  current_uses?: number;
  is_public?: boolean;
  discount_display: string;
  is_valid_now: boolean;
  created_at: string;
  updated_at: string;
}

export interface PromotionStats {
  total_promotions: number;
  active_promotions: number;
  valid_promotions: number;
  expired_promotions: number;
  discount_types: {
    percentage: number;
    fixed_amount: number;
    buy_x_get_y: number;
  };
}

export interface CreatePromotionData {
  name: string;
  code: string;
  description?: string;
  discount_type: 'percentage' | 'fixed_amount' | 'buy_x_get_y';
  discount_value: number;
  buy_quantity?: number;
  get_quantity?: number;
  buy_service_id?: string;
  get_service_id?: string;
  status?: 'active' | 'inactive' | 'expired';
  expiration_date: string;
  is_public?: boolean;
}

export interface UpdatePromotionData extends Partial<CreatePromotionData> {}

export interface PromotionValidation {
  valid: boolean;
  promotion?: Promotion;
  error?: string;
  reasons?: string[];
}

export interface DiscountCalculation {
  discount: number;
  promotion: Promotion;
  final_amount: number;
}

class PromotionApiService {
  /**
   * Récupère toutes les promotions
   */
  async getPromotions(filters?: {
    status?: string;
    is_valid?: boolean;
    is_public?: boolean;
  }): Promise<Promotion[]> {
    try {
      const params: any = {};
      if (filters?.status) params.status = filters.status;
      if (filters?.is_valid !== undefined) params.is_valid = filters.is_valid;
      if (filters?.is_public !== undefined) params.is_public = filters.is_public;

      const response = await api.get('/api/promotions/', { params });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      console.error('Erreur lors de la récupération des promotions:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la récupération des promotions');
    }
  }

  /**
   * Récupère les promotions actives
   */
  async getActivePromotions(): Promise<Promotion[]> {
    try {
      const response = await api.get('/api/promotions/', { 
        params: { status: 'active' } 
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      console.error('Erreur lors de la récupération des promotions actives:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la récupération des promotions actives');
    }
  }

  /**
   * Récupère une promotion par son ID
   */
  async getPromotion(id: number): Promise<Promotion> {
    try {
      const response = await api.get(`/api/promotions/${id}/`);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la récupération de la promotion:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la récupération de la promotion');
    }
  }

  /**
   * Crée une nouvelle promotion
   */
  async createPromotion(promotionData: CreatePromotionData): Promise<Promotion> {
    try {
      const response = await api.post('/api/promotions/', promotionData);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la création de la promotion:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      if (error.response?.data) {
        // Erreurs de validation
        const errors = Object.entries(error.response.data)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('; ');
        throw new Error(errors);
      }
      throw new Error('Erreur lors de la création de la promotion');
    }
  }

  /**
   * Met à jour une promotion
   */
  async updatePromotion(id: number, promotionData: UpdatePromotionData): Promise<Promotion> {
    try {
      const response = await api.patch(`/api/promotions/${id}/`, promotionData);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour de la promotion:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      if (error.response?.data) {
        // Erreurs de validation
        const errors = Object.entries(error.response.data)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('; ');
        throw new Error(errors);
      }
      throw new Error('Erreur lors de la mise à jour de la promotion');
    }
  }

  /**
   * Supprime une promotion
   */
  async deletePromotion(id: number): Promise<void> {
    try {
      await api.delete(`/api/promotions/${id}/`);
    } catch (error: any) {
      console.error('Erreur lors de la suppression de la promotion:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la suppression de la promotion');
    }
  }

  /**
   * Active/désactive une promotion
   */
  async togglePromotionStatus(id: number): Promise<Promotion> {
    try {
      const response = await api.post(`/api/promotions/${id}/toggle-status/`);
      return response.data.promotion;
    } catch (error: any) {
      console.error('Erreur lors du changement de statut:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors du changement de statut');
    }
  }

  /**
   * Récupère les statistiques des promotions
   */
  async getPromotionStats(): Promise<PromotionStats> {
    try {
      const response = await api.get('/api/promotions/stats/');
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la récupération des statistiques');
    }
  }

  /**
   * Valide un code promo
   */
  async validatePromotionCode(code: string): Promise<PromotionValidation> {
    try {
      const response = await api.post('/api/promotions/validate-code/', { code });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 400 || error.response?.status === 404) {
        return error.response.data;
      }
      console.error('Erreur lors de la validation du code promo:', error);
      throw new Error('Erreur lors de la validation du code promo');
    }
  }

  /**
   * Calcule la réduction pour un panier
   */
  async calculateDiscount(
    code: string,
    totalAmount: number,
    serviceIds?: number[],
    quantities?: { [key: string]: number }
  ): Promise<DiscountCalculation> {
    try {
      const response = await api.post('/api/promotions/calculate-discount/', {
        code,
        total_amount: totalAmount,
        service_ids: serviceIds,
        quantities
      });
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors du calcul de la réduction:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors du calcul de la réduction');
    }
  }

  /**
   * Formate l'affichage d'une promotion
   */
  formatPromotionDisplay(promotion: Promotion): string {
    return promotion.discount_display;
  }

  /**
   * Vérifie si une promotion est actuellement valide
   */
  isPromotionValid(promotion: Promotion): boolean {
    const now = new Date();
    const expirationDate = new Date(promotion.expiration_date);
    
    return (
      promotion.status === 'active' &&
      now <= expirationDate
    );
  }

  /**
   * Formate la date d'expiration pour l'affichage
   */
  formatExpirationDate(promotion: Promotion): string {
    try {
      if (!promotion.expiration_date) {
        return 'Date non définie';
      }
      
      const expirationDate = new Date(promotion.expiration_date);
      
      // Vérifier si la date est valide
      if (isNaN(expirationDate.getTime())) {
        return 'Date invalide';
      }
      
      const formatted = expirationDate.toLocaleDateString('fr-FR');
      
      return `Expire le ${formatted}`;
    } catch (error) {
      console.error('Erreur lors du formatage de la date:', error);
      return 'Erreur de format de date';
    }
  }

  /**
   * Enregistre un code promo gagné par l'utilisateur
   */
  async saveUserPromoCode(promotionId: number, source: string = 'wheel'): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/api/promotions/user-codes/', {
        promotion_id: promotionId,
        source: source,
        status: 'active'
      });
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde du code promo:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la sauvegarde du code promo');
    }
  }

  /**
   * Récupère les codes promo actifs de l'utilisateur
   */
  async getUserPromoCodes(): Promise<Promotion[]> {
    try {
      const response = await api.get('/api/promotions/user-codes/');
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la récupération des codes promo utilisateur:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la récupération des codes promo');
    }
  }

  /**
   * Génère un code promo aléatoire
   */
  generatePromotionCode(prefix: string = 'PROMO'): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = prefix;
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * Valide les données d'une promotion
   */
  validatePromotionData(data: CreatePromotionData | UpdatePromotionData): string[] {
    const errors: string[] = [];

    if ('name' in data && (!data.name || data.name.trim().length === 0)) {
      errors.push('Le nom de la promotion est requis');
    }

    if ('code' in data && (!data.code || data.code.trim().length === 0)) {
      errors.push('Le code promo est requis');
    }

    if ('discount_value' in data && (data.discount_value === undefined || data.discount_value < 0)) {
      errors.push('La valeur de réduction doit être positive');
    }

    if ('discount_type' in data && data.discount_type === 'percentage' && data.discount_value && data.discount_value > 100) {
      errors.push('Le pourcentage ne peut pas dépasser 100%');
    }

    if ('discount_type' in data && data.discount_type === 'buy_x_get_y') {
      if (!data.buy_quantity || data.buy_quantity <= 0) {
        errors.push('La quantité à acheter (X) est requise pour ce type de promotion');
      }
      if (!data.get_quantity || data.get_quantity <= 0) {
        errors.push('La quantité gratuite (Y) est requise pour ce type de promotion');
      }
    }

    if ('expiration_date' in data && data.expiration_date) {
      const expirationDate = new Date(data.expiration_date);
      const now = new Date();
      if (expirationDate <= now) {
        errors.push('La date d\'expiration doit être dans le futur');
      }
    }

    return errors;
  }

  // Valider un code promo
  async validatePromoCode(code: string): Promise<{valid: boolean, promotion?: Promotion, error?: string}> {
    try {
      const response = await api.post('/api/promotions/validate-code/', { code });
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la validation du code promo:', error);
      if (error.response?.data) {
        return error.response.data;
      }
      return { valid: false, error: 'Erreur lors de la validation du code promo' };
    }
  }

  /**
   * Passe un code promo en statut pending (facture créée)
   */
  async setPromoCodePending(code: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/api/promotions/set-pending/', { code });
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors du passage en pending du code promo:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors du traitement du code promo');
    }
  }

  /**
   * Réactive un code promo pending (facture annulée sans confirmation)
   */
  async reactivatePromoCode(code: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/api/promotions/reactivate/', { code });
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la réactivation du code promo:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la réactivation du code promo');
    }
  }

  /**
   * Désactive définitivement un code promo (facture confirmée)
   */
  async deactivateUserPromoCode(code: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/api/promotions/deactivate-user-code/', { code });
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la désactivation du code promo:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la désactivation du code promo');
    }
  }
}

export const promotionApiService = new PromotionApiService();
